import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, RefreshControl } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api, apiGet, apiPost } from '../../src/api/client';
import VehicleProfileModal, { SubscriptionVehicle, VehicleFields } from '../../src/components/VehicleProfileModal';
import Card from '../../src/components/Card';
import Button from '../../src/components/Button';
import InlineConfirm from '../../src/components/InlineConfirm';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import type { ColorTheme } from '../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { rs, fs } from '../../src/utils/responsive';
import ContentContainer from '../../src/components/ContentContainer';
import { formatDateTime } from '../../src/utils/timezone';
import { vacMinutesLabel, vendingCreditLabel } from '../../src/utils/planBenefits';

interface Subscription {
  id: string;
  status: string;
  suspended?: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  washesUsedToday: number;
  washesUsedThisMonth: number;
  minutesUsedToday?: number;
  minutesUsedThisMonth?: number;
  vacMinutesUsedToday?: number;
  vacMinutesUsedThisMonth?: number;
  vendingCreditUsedThisMonth?: number;
  lastWashDate: string | null;
  createdAt: string;
  plan: {
    id: string;
    name: string;
    description: string | null;
    planType?: 'automatic' | 'self_serve' | 'mixed';
    tierName: string | null;
    priceMonthly: number;
    maxWashesPerDay: number | null;
    maxWashesPerMonth: number | null;
    maxMinutesPerDay: number | null;
    maxMinutesPerMonth: number | null;
    // Vacuum minutes + vending credit (null = not included; cap 0 = unlimited).
    maxVacMinutesPerDay?: number | null;
    maxVacMinutesPerMonth?: number | null;
    vendingCreditCentsPerMonth?: number | null;
    locationScope: string;
    locationName: string | null;
  } | null;
  ownerBusinessName: string;
  ownerId: string;
  vehicle?: SubscriptionVehicle | null;
  pendingPlanChange?: {
    planId: string;
    name: string;
    tierName: string | null;
    priceMonthly: number;
    effectiveAt: string | null;
  } | null;
}

interface ChangeOption {
  id: string;
  name: string;
  tierName: string | null;
  planType?: string;
  priceMonthly: number;
  maxVacMinutesPerDay?: number | null;
  maxVacMinutesPerMonth?: number | null;
  vendingCreditCentsPerMonth?: number | null;
  changeType: 'upgrade' | 'downgrade' | 'lateral';
}

interface ChangeOptions {
  allowed: boolean;
  reason?: string;
  currentPeriodEnd?: string | null;
  options?: ChangeOption[];
}

interface PurchasedBundleItem {
  kind: 'wash' | 'vac_minutes' | 'vending_credit';
  tierName: string | null;
  totalQuantity: number | null;
  usedQuantity: number;
  totalMinutes: number | null;
  usedMinutes: number;
  totalCreditCents: number | null;
  usedCreditCents: number;
  // vac_minutes only: must be used in a single session.
  allAtOnce?: boolean;
}

interface PurchasedBundle {
  id: string;
  status: string;
  washesTotal: number;
  washesUsed: number;
  washesRemaining: number;
  tierName: string;
  items?: PurchasedBundleItem[];
  locationScope: string;
  locationName: string | null;
  expiresAt: string | null;
  purchasedAt: string;
  planName: string;
  ownerBusinessName: string;
}

// Vacuum minutes remaining shown with one decimal ("12.5"), trailing .0
// stripped ("12") — Nathan 2026-08-17.
function formatMinutes1(n: number): string {
  const s = Math.max(0, n).toFixed(1);
  return s.endsWith('.0') ? s.slice(0, -2) : s;
}

/** Per-item label + remaining for a purchased multi-item bundle row. */
function purchasedItemLine(i: PurchasedBundleItem): { label: string; remaining: string } {
  if (i.kind === 'wash') {
    const total = i.totalQuantity ?? 0;
    return {
      label: `${total}× ${i.tierName || 'wash'} wash${total === 1 ? '' : 'es'}`,
      remaining: `${Math.max(0, total - i.usedQuantity)} left`,
    };
  }
  if (i.kind === 'vac_minutes') {
    const total = i.totalMinutes ?? 0;
    return {
      label: `${total} vacuum minutes${i.allAtOnce ? ' (one session)' : ''}`,
      remaining: `${formatMinutes1(total - i.usedMinutes)} min left`,
    };
  }
  const total = i.totalCreditCents ?? 0;
  return {
    label: `$${(total / 100).toFixed(2)} vending credit`,
    remaining: `$${(Math.max(0, total - i.usedCreditCents) / 100).toFixed(2)} left`,
  };
}

/** UserSubscriptionsScreen - active subscriptions, wash bundles, and available plans to purchase. */
export default function UserSubscriptionsScreen() {
  const [activeSubs, setActiveSubs] = useState<Subscription[]>([]);
  const [pastSubs, setPastSubs] = useState<Subscription[]>([]);
  const [pastTotal, setPastTotal] = useState(0);
  const [pastPage, setPastPage] = useState(1);
  const [pastLoading, setPastLoading] = useState(false);
  const [bundles, setBundles] = useState<PurchasedBundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<string | null>(null);
  // Plan switching (owner-configurable): ownerId → change options.
  const [changeOptions, setChangeOptions] = useState<Record<string, ChangeOptions>>({});
  const [showChangeFor, setShowChangeFor] = useState<string | null>(null);
  const [confirmChange, setConfirmChange] = useState<{ subId: string; ownerId: string; option: ChangeOption } | null>(null);
  const [changing, setChanging] = useState(false);
  // Registered-vehicle editing (mig 0055): which subscription's vehicle is open.
  const [vehicleEditSub, setVehicleEditSub] = useState<Subscription | null>(null);
  const toast = useToast();
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [subData, bundleData] = await Promise.all([
        apiGet<{ active: Subscription[]; past: Subscription[]; pastTotal: number }>('/api/subscriptions/mine?pastPage=1&pastLimit=10'),
        apiGet<PurchasedBundle[]>('/api/bundles/mine'),
      ]);
      setActiveSubs(subData.active);
      setPastSubs(subData.past);
      setPastTotal(subData.pastTotal);
      setPastPage(1);
      setBundles(bundleData);
      // Best-effort: what (if anything) each membership can switch to.
      const opts: Record<string, ChangeOptions> = {};
      await Promise.all(subData.active
        .filter(s => s.status !== 'canceled' && s.ownerId)
        .map(async s => {
          try {
            opts[s.ownerId] = await apiGet<ChangeOptions>(`/api/subscriptions/change-options?ownerId=${s.ownerId}`);
          } catch { /* switching UI simply stays hidden */ }
        }));
      setChangeOptions(opts);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  /** Fetch a page of past (canceled) memberships. */
  const fetchPastSubs = async (page = 1) => {
    setPastLoading(true);
    try {
      const data = await apiGet<{ active: Subscription[]; past: Subscription[]; pastTotal: number }>(`/api/subscriptions/mine?pastPage=${page}&pastLimit=10`);
      setPastSubs(data.past);
      setPastTotal(data.pastTotal);
      setPastPage(page);
    } catch { /* ignore */ }
    finally { setPastLoading(false); }
  };

  useFocusEffect(useCallback(() => { fetchData(); }, []));

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const handleCancel = async (sub: Subscription) => {
    setCanceling(sub.id);
    try {
      await apiPost(`/api/subscriptions/${sub.id}/cancel`);
      toast.show({ type: 'success', title: 'Membership canceled', message: 'Access continues until end of billing period.' });
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setCanceling(null);
    }
  };

  const handleChangePlan = async () => {
    if (!confirmChange) return;
    setChanging(true);
    try {
      const res = await apiPost<{ changed: 'immediate' | 'at_renewal'; effectiveAt?: string | null }>(
        '/api/subscriptions/change-plan', { newPlanId: confirmChange.option.id });
      if (res.changed === 'immediate') {
        toast.show({ type: 'success', title: 'Plan changed!', message: `You're now on ${confirmChange.option.name}.` });
      } else {
        toast.show({ type: 'success', title: 'Change scheduled', message: `${confirmChange.option.name} starts at your next billing date.` });
      }
      setConfirmChange(null);
      setShowChangeFor(null);
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Could not change plan', message: err.message });
    } finally {
      setChanging(false);
    }
  };

  const handleCancelPlanChange = async (ownerId: string) => {
    try {
      await apiPost('/api/subscriptions/cancel-plan-change', { ownerId });
      toast.show({ type: 'success', title: 'Scheduled change canceled', message: 'You’ll stay on your current plan.' });
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleVehicleSave = async (fields: VehicleFields) => {
    if (!vehicleEditSub) return;
    await api(`/api/subscriptions/${vehicleEditSub.id}/vehicle`, { method: 'PUT', body: fields });
    setVehicleEditSub(null);
    toast.show({ type: 'success', title: 'Vehicle saved', message: 'Your registered vehicle has been updated.' });
    fetchData();
  };

  const changeConfirmMessage = (option: ChangeOption, periodEnd?: string | null) => {
    if (option.changeType === 'downgrade') {
      return `Switch to ${option.name} (${fmt(option.priceMonthly)}/mo)? Your current plan stays active until ${periodEnd ? formatDateTime(periodEnd) : 'the end of this billing period'}; the new plan and price start then.`;
    }
    return `Switch to ${option.name} (${fmt(option.priceMonthly)}/mo) now? Your new plan starts immediately and you'll be charged a prorated amount for the rest of this billing period.`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return colors.success;
      case 'past_due': return colors.warning;
      case 'canceled': return colors.danger;
      default: return colors.textSecondary;
    }
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={<RefreshControl refreshing={loading} onRefresh={fetchData} />}
    >
      <ContentContainer maxWidth={720}>
      {activeSubs.length === 0 && pastSubs.length === 0 && bundles.length === 0 && !loading && (
        <Card style={styles.emptyCard}>
          <Ionicons name="ribbon-outline" size={48} color={colors.textLight} />
          <Text style={styles.emptyTitle}>No Memberships or Bundles</Text>
          <Text style={styles.emptyText}>
            When you scan a QR code at a participating car wash, you'll see available membership plans and wash bundles.
          </Text>
        </Card>
      )}

      {activeSubs.map(sub => (
        <Card key={sub.id} style={styles.subCard}>
          <View style={styles.subHeader}>
            <View style={{ flex: 1 }}>
              <Text style={styles.subPlanName}>{sub.plan?.name || 'Membership'}</Text>
              <Text style={styles.subBusiness}>{sub.ownerBusinessName}</Text>
            </View>
            <View style={[styles.statusBadge, { backgroundColor: (sub.suspended ? colors.danger : getStatusColor(sub.status)) + '20' }]}>
              <Text style={[styles.statusText, { color: sub.suspended ? colors.danger : getStatusColor(sub.status) }]}>
                {sub.suspended ? 'Suspended' : sub.cancelAtPeriodEnd ? 'Canceling' : sub.status}
              </Text>
            </View>
          </View>

          {sub.suspended && (
            <View style={[styles.cancelNotice, { backgroundColor: colors.danger + '10' }]}>
              <Ionicons name="pause-circle" size={16} color={colors.danger} />
              <Text style={[styles.cancelNoticeText, { color: colors.danger }]}>
                Your membership is suspended — washes are unavailable and billing is paused. Please contact the car wash.
              </Text>
            </View>
          )}

          {sub.plan && (
            <>
              {sub.plan.tierName && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Tier</Text>
                  <Text style={styles.detailValue}>{/^[1-4]$/.test(sub.plan.tierName) ? `Option ${sub.plan.tierName}` : sub.plan.tierName}</Text>
                </View>
              )}
              {(sub.plan.planType === 'self_serve' || sub.plan.planType === 'mixed') && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Type</Text>
                  <Text style={styles.detailValue}>{sub.plan.planType === 'self_serve' ? 'Self-serve' : 'Mixed (auto + self-serve)'}</Text>
                </View>
              )}
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Price</Text>
                <Text style={styles.detailValue}>{fmt(sub.plan.priceMonthly)}/month</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Scope</Text>
                <Text style={styles.detailValue}>
                  {sub.plan.locationScope === 'all' ? 'All locations' : sub.plan.locationName || 'Single location'}
                </Text>
              </View>

              {/* Wash usage — automatic / mixed plans */}
              {((sub.plan.planType ?? 'automatic') !== 'self_serve') && sub.plan.maxWashesPerDay != null && sub.plan.maxWashesPerMonth != null && (
                <View style={styles.usageSection}>
                  <Text style={styles.usageTitle}>Washes Remaining</Text>
                  <View style={styles.usageRow}>
                    <View style={styles.usageItem}>
                      <Text style={styles.usageCount}>{sub.plan.maxWashesPerDay === 0 ? '∞' : sub.plan.maxWashesPerDay - sub.washesUsedToday}</Text>
                      <Text style={styles.usageLabel}>today</Text>
                    </View>
                    <View style={styles.usageItem}>
                      <Text style={styles.usageCount}>{sub.plan.maxWashesPerMonth === 0 ? '∞' : sub.plan.maxWashesPerMonth - sub.washesUsedThisMonth}</Text>
                      <Text style={styles.usageLabel}>this month</Text>
                    </View>
                  </View>
                </View>
              )}

              {/* Minute usage — self_serve / mixed plans */}
              {(sub.plan.planType === 'self_serve' || sub.plan.planType === 'mixed') && sub.plan.maxMinutesPerDay != null && sub.plan.maxMinutesPerMonth != null && (
                <View style={styles.usageSection}>
                  <Text style={styles.usageTitle}>Self-Serve Minutes Remaining</Text>
                  <View style={styles.usageRow}>
                    <View style={styles.usageItem}>
                      <Text style={styles.usageCount}>{sub.plan.maxMinutesPerDay === 0 ? '∞' : Math.max(0, sub.plan.maxMinutesPerDay - (sub.minutesUsedToday ?? 0)).toFixed(1)}</Text>
                      <Text style={styles.usageLabel}>today</Text>
                    </View>
                    <View style={styles.usageItem}>
                      <Text style={styles.usageCount}>{sub.plan.maxMinutesPerMonth === 0 ? '∞' : Math.max(0, sub.plan.maxMinutesPerMonth - (sub.minutesUsedThisMonth ?? 0)).toFixed(1)}</Text>
                      <Text style={styles.usageLabel}>this month</Text>
                    </View>
                  </View>
                </View>
              )}

              {/* Vacuum minutes remaining — plans with a vac bucket (cap 0 = unlimited) */}
              {(sub.plan.maxVacMinutesPerDay != null || sub.plan.maxVacMinutesPerMonth != null) && (
                <View style={styles.usageSection}>
                  <Text style={styles.usageTitle}>Vacuum Minutes Remaining</Text>
                  <View style={styles.usageRow}>
                    <View style={styles.usageItem}>
                      <Text style={styles.usageCount}>
                        {sub.plan.maxVacMinutesPerDay == null || sub.plan.maxVacMinutesPerDay <= 0
                          ? '∞'
                          : formatMinutes1(sub.plan.maxVacMinutesPerDay - (sub.vacMinutesUsedToday ?? 0))}
                      </Text>
                      <Text style={styles.usageLabel}>today</Text>
                    </View>
                    <View style={styles.usageItem}>
                      <Text style={styles.usageCount}>
                        {sub.plan.maxVacMinutesPerMonth == null || sub.plan.maxVacMinutesPerMonth <= 0
                          ? '∞'
                          : formatMinutes1(sub.plan.maxVacMinutesPerMonth - (sub.vacMinutesUsedThisMonth ?? 0))}
                      </Text>
                      <Text style={styles.usageLabel}>this month</Text>
                    </View>
                  </View>
                </View>
              )}

              {/* Monthly vending credit — remaining this billing month */}
              {sub.plan.vendingCreditCentsPerMonth != null && sub.plan.vendingCreditCentsPerMonth > 0 && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Vending credit</Text>
                  <Text style={styles.detailValue}>
                    {fmt(Math.max(0, sub.plan.vendingCreditCentsPerMonth - (sub.vendingCreditUsedThisMonth ?? 0)))} left of {fmt(sub.plan.vendingCreditCentsPerMonth)}/mo
                  </Text>
                </View>
              )}

              {sub.currentPeriodEnd && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>
                    {sub.cancelAtPeriodEnd ? 'Access until' : 'Renews'}
                  </Text>
                  <Text style={styles.detailValue}>{formatDateTime(sub.currentPeriodEnd)}</Text>
                </View>
              )}
            </>
          )}

          {/* Registered vehicle (mig 0055) — one vehicle per membership */}
          {sub.status !== 'canceled' && (
            <View style={styles.vehicleSection}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.xs }}>
                <Ionicons name="car-outline" size={16} color={colors.primary} />
                <Text style={styles.vehicleTitle}>Registered Vehicle</Text>
              </View>
              {sub.vehicle ? (
                <>
                  <Text style={styles.vehicleDesc}>
                    {sub.vehicle.description} — plate {sub.vehicle.licensePlate}
                  </Text>
                  {sub.vehicle.canEdit ? (
                    <Button
                      title="Edit Vehicle"
                      variant="outline"
                      size="sm"
                      style={{ marginTop: spacing.sm }}
                      icon={<Ionicons name="create-outline" size={14} color={colors.primary} />}
                      onPress={() => setVehicleEditSub(sub)}
                    />
                  ) : (
                    <Text style={styles.vehicleLockText}>
                      Changes locked until {sub.vehicle.nextEditAt ? formatDateTime(sub.vehicle.nextEditAt) : 'later'} (one change every 15 days). Contact the wash if this is wrong.
                    </Text>
                  )}
                </>
              ) : (
                <>
                  <Text style={styles.vehicleDesc}>
                    Your membership is valid for one registered vehicle. Add yours now — you'll be asked for it at your next membership wash.
                  </Text>
                  <Button
                    title="Add Vehicle"
                    variant="outline"
                    size="sm"
                    style={{ marginTop: spacing.sm }}
                    icon={<Ionicons name="car-outline" size={14} color={colors.primary} />}
                    onPress={() => setVehicleEditSub(sub)}
                  />
                </>
              )}
            </View>
          )}

          {/* Scheduled downgrade notice */}
          {sub.pendingPlanChange && (
            <View style={styles.changeNotice}>
              <Ionicons name="swap-horizontal" size={16} color={colors.primary} />
              <Text style={styles.changeNoticeText}>
                Switching to {sub.pendingPlanChange.name} ({fmt(sub.pendingPlanChange.priceMonthly)}/mo)
                {sub.pendingPlanChange.effectiveAt ? ` on ${formatDateTime(sub.pendingPlanChange.effectiveAt)}` : ' at your next billing date'}.
              </Text>
              <TouchableOpacity onPress={() => handleCancelPlanChange(sub.ownerId)} hitSlop={8}>
                <Text style={styles.changeNoticeUndo}>Undo</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Plan switching — only when the owner has enabled it */}
          {sub.status !== 'canceled' && !sub.cancelAtPeriodEnd && !sub.pendingPlanChange
            && changeOptions[sub.ownerId]?.allowed && (changeOptions[sub.ownerId]?.options?.length ?? 0) > 0 && (
            <>
              <Button
                title={showChangeFor === sub.id ? 'Hide Plans' : 'Change Plan'}
                onPress={() => setShowChangeFor(showChangeFor === sub.id ? null : sub.id)}
                variant="outline"
                size="sm"
                style={{ marginTop: spacing.md }}
                icon={<Ionicons name="swap-horizontal" size={16} color={colors.primary} />}
              />
              {showChangeFor === sub.id && (changeOptions[sub.ownerId]?.options ?? []).map(option => (
                <View key={option.id} style={styles.changeOptionRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.changeOptionName}>{option.name}</Text>
                    <Text style={styles.changeOptionMeta}>
                      {fmt(option.priceMonthly)}/mo{option.tierName ? ` · ${option.tierName}` : ''}
                    </Text>
                    {/* Vac/vending benefits stay visible wherever a plan is purchasable */}
                    {(vacMinutesLabel(option.maxVacMinutesPerDay, option.maxVacMinutesPerMonth)
                      || vendingCreditLabel(option.vendingCreditCentsPerMonth)) && (
                      <Text style={[styles.changeOptionMeta, { color: colors.primary }]}>
                        {[
                          vacMinutesLabel(option.maxVacMinutesPerDay, option.maxVacMinutesPerMonth),
                          vendingCreditLabel(option.vendingCreditCentsPerMonth),
                        ].filter(Boolean).join(' · ')}
                      </Text>
                    )}
                  </View>
                  <View style={[styles.changeTypeBadge, {
                    backgroundColor: (option.changeType === 'upgrade' ? colors.success : option.changeType === 'downgrade' ? colors.warning : colors.primary) + '20',
                  }]}>
                    <Text style={[styles.changeTypeText, {
                      color: option.changeType === 'upgrade' ? colors.success : option.changeType === 'downgrade' ? colors.warning : colors.primary,
                    }]}>
                      {option.changeType === 'upgrade' ? 'Upgrade' : option.changeType === 'downgrade' ? 'Downgrade' : 'Same price'}
                    </Text>
                  </View>
                  <Button
                    title="Select"
                    size="sm"
                    onPress={() => setConfirmChange({ subId: sub.id, ownerId: sub.ownerId, option })}
                  />
                </View>
              ))}
              {confirmChange?.subId === sub.id && (
                <InlineConfirm
                  message={changeConfirmMessage(confirmChange.option, changeOptions[sub.ownerId]?.currentPeriodEnd ?? sub.currentPeriodEnd)}
                  visible={true}
                  onConfirm={handleChangePlan}
                  onCancel={() => setConfirmChange(null)}
                  confirmLabel={confirmChange.option.changeType === 'downgrade' ? 'Schedule Change' : 'Switch Now'}
                  loading={changing}
                />
              )}
            </>
          )}

          {sub.status !== 'canceled' && !sub.cancelAtPeriodEnd && (
            <>
              <Button
                title="Cancel Membership"
                onPress={() => setConfirmCancel(sub.id)}
                loading={canceling === sub.id}
                variant="outline"
                size="sm"
                style={{ marginTop: spacing.md }}
              />
              {confirmCancel === sub.id && (
                <InlineConfirm
                  message={`Cancel your ${sub.plan?.name || 'membership'}? You'll keep access until the end of your billing period.`}
                  visible={true}
                  onConfirm={async () => {
                    setConfirmCancel(null);
                    await handleCancel(sub);
                  }}
                  onCancel={() => setConfirmCancel(null)}
                  confirmLabel="Cancel Membership"
                  loading={canceling === sub.id}
                  variant="danger"
                />
              )}
            </>
          )}

          {sub.cancelAtPeriodEnd && (
            <>
              <View style={styles.cancelNotice}>
                <Ionicons name="information-circle" size={16} color={colors.warning} />
                <Text style={styles.cancelNoticeText}>
                  Your membership will end at the end of the current billing period. Plan changes are unavailable while a cancellation is scheduled.
                </Text>
              </View>
              <Button
                title="Resume Membership"
                variant="outline"
                size="sm"
                style={{ marginTop: spacing.sm }}
                icon={<Ionicons name="refresh" size={14} color={colors.primary} />}
                onPress={async () => {
                  try {
                    await apiPost(`/api/subscriptions/${sub.id}/resume`);
                    toast.show({ type: 'success', title: 'Membership resumed', message: 'Your membership will renew as normal.' });
                    fetchData();
                  } catch (err: any) {
                    toast.show({ type: 'error', title: 'Error', message: err.message });
                  }
                }}
              />
            </>
          )}
        </Card>
      ))}

      {/* My Bundles */}
      {bundles.filter(b => b.status === 'active').length > 0 && (
        <>
          <Text style={styles.sectionTitle}>My Bundles</Text>
          {bundles.filter(b => b.status === 'active').map(b => (
            <Card key={b.id} style={styles.subCard}>
              <View style={styles.subHeader}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                    <Ionicons name="cube" size={16} color={colors.primary} />
                    <Text style={styles.subPlanName}>{b.planName}</Text>
                  </View>
                  <Text style={styles.subBusiness}>{b.ownerBusinessName}</Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: colors.success + '20' }]}>
                  <Text style={[styles.statusText, { color: colors.success }]}>active</Text>
                </View>
              </View>

              {!(b.items && b.items.length > 0) && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Tier</Text>
                  <Text style={styles.detailValue}>{/^[1-4]$/.test(b.tierName) ? `Option ${b.tierName}` : b.tierName}</Text>
                </View>
              )}
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Scope</Text>
                <Text style={styles.detailValue}>
                  {b.locationScope === 'all' ? 'All locations' : b.locationName || 'Single location'}
                </Text>
              </View>

              {/* Usage — per-item rows for multi-item bundles, wash bar for legacy */}
              {b.items && b.items.length > 0 ? (
                <View style={styles.usageSection}>
                  <Text style={styles.usageTitle}>What's Left</Text>
                  {b.items.map((it, idx) => {
                    const line = purchasedItemLine(it);
                    return (
                      <View key={idx} style={styles.detailRow}>
                        <Text style={styles.detailLabel}>{line.label}</Text>
                        <Text style={[styles.detailValue, { color: colors.primary }]}>{line.remaining}</Text>
                      </View>
                    );
                  })}
                </View>
              ) : (
                <View style={styles.usageSection}>
                  <Text style={styles.usageTitle}>Washes Remaining</Text>
                  <View style={styles.bundleBarContainer}>
                    <View style={[styles.bundleBarFill, { width: `${b.washesTotal > 0 ? (b.washesRemaining / b.washesTotal) * 100 : 0}%` }]} />
                  </View>
                  <View style={styles.usageRow}>
                    <View style={styles.usageItem}>
                      <Text style={styles.usageCount}>{b.washesRemaining}</Text>
                      <Text style={styles.usageLabel}>remaining</Text>
                    </View>
                    <View style={styles.usageItem}>
                      <Text style={[styles.usageCount, { color: colors.textSecondary }]}>{b.washesUsed}</Text>
                      <Text style={styles.usageLabel}>used</Text>
                    </View>
                    <View style={styles.usageItem}>
                      <Text style={[styles.usageCount, { color: colors.textSecondary }]}>{b.washesTotal}</Text>
                      <Text style={styles.usageLabel}>total</Text>
                    </View>
                  </View>
                </View>
              )}

              {b.expiresAt && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Expires</Text>
                  <Text style={styles.detailValue}>{formatDateTime(b.expiresAt)}</Text>
                </View>
              )}
            </Card>
          ))}
        </>
      )}

      {/* Past/used bundles */}
      {bundles.filter(b => b.status !== 'active').length > 0 && (
        <>
          <Text style={styles.sectionTitle}>Past Bundles</Text>
          {bundles.filter(b => b.status !== 'active').map(b => (
            <Card key={b.id} style={[styles.subCard, { opacity: 0.6 }]}>
              <View style={styles.subHeader}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                    <Ionicons name="cube" size={16} color={colors.textSecondary} />
                    <Text style={styles.subPlanName}>{b.planName}</Text>
                  </View>
                  <Text style={styles.subBusiness}>{b.ownerBusinessName}</Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: (b.status === 'refunded' ? colors.danger : colors.textSecondary) + '20' }]}>
                  <Text style={[styles.statusText, { color: b.status === 'refunded' ? colors.danger : colors.textSecondary }]}>{b.status}</Text>
                </View>
              </View>
              <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                {b.items && b.items.length > 0
                  ? b.items.map(it => purchasedItemLine(it).label).join(' · ')
                  : `${b.washesUsed}/${b.washesTotal} washes used`}
              </Text>
            </Card>
          ))}
        </>
      )}

      {pastSubs.length > 0 && (
        <>
          <Text style={styles.sectionTitle}>Past Memberships{pastTotal > 0 ? ` (${pastTotal})` : ''}</Text>
          {pastSubs.map(sub => (
            <Card key={sub.id} style={[styles.subCard, { opacity: 0.6 }]}>
              <View style={styles.subHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.subPlanName}>{sub.plan?.name || 'Membership'}</Text>
                  <Text style={styles.subBusiness}>{sub.ownerBusinessName}</Text>
                  <Text style={styles.subBusiness}>Started {formatDateTime(sub.createdAt)}</Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: colors.danger + '20' }]}>
                  <Text style={[styles.statusText, { color: colors.danger }]}>Canceled</Text>
                </View>
              </View>
            </Card>
          ))}
          {pastTotal > 10 && (
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
              <Button
                title="Previous"
                onPress={() => fetchPastSubs(pastPage - 1)}
                disabled={pastPage <= 1 || pastLoading}
                size="sm"
                variant="outline"
              />
              <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>Page {pastPage} of {Math.ceil(pastTotal / 10)}</Text>
              <Button
                title="Next"
                onPress={() => fetchPastSubs(pastPage + 1)}
                disabled={pastPage >= Math.ceil(pastTotal / 10) || pastLoading}
                size="sm"
                variant="outline"
              />
            </View>
          )}
        </>
      )}

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>

      <VehicleProfileModal
        visible={vehicleEditSub !== null}
        mode="customer"
        initial={vehicleEditSub?.vehicle ?? null}
        contextLine={vehicleEditSub ? `${vehicleEditSub.plan?.name || 'Membership'} — ${vehicleEditSub.ownerBusinessName}` : undefined}
        onSubmit={handleVehicleSave}
        onClose={() => setVehicleEditSub(null)}
      />
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },

  emptyCard: { alignItems: 'center', padding: spacing.xl },
  emptyTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.md },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.sm, maxWidth: rs(280) },

  subCard: { marginBottom: spacing.md },
  subHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.sm },
  subPlanName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  subBusiness: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  statusBadge: { paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: borderRadius.full },
  statusText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium },

  detailRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: spacing.xs, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  detailLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  detailValue: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },

  usageSection: { marginTop: spacing.sm, padding: spacing.md, backgroundColor: c.primary + '08', borderRadius: borderRadius.md },
  usageTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, marginBottom: spacing.sm, textAlign: 'center' },
  usageRow: { flexDirection: 'row', justifyContent: 'space-around' },
  usageItem: { alignItems: 'center' },
  usageCount: { fontSize: fs(28), fontWeight: fontWeight.bold, color: c.primary },
  usageLabel: { fontSize: fontSize.xs, color: c.textSecondary },

  vehicleSection: { marginTop: spacing.sm, padding: spacing.md, backgroundColor: c.primary + '08', borderRadius: borderRadius.md },
  vehicleTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  vehicleDesc: { fontSize: fontSize.sm, color: c.textSecondary, lineHeight: 20 },
  vehicleLockText: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.xs, lineHeight: 18 },

  cancelNotice: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.md, padding: spacing.sm, backgroundColor: c.warning + '10', borderRadius: borderRadius.md },
  cancelNoticeText: { fontSize: fontSize.xs, color: c.warning, flex: 1 },

  changeNotice: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.md, padding: spacing.sm, backgroundColor: c.primary + '08', borderRadius: borderRadius.md },
  changeNoticeText: { fontSize: fontSize.xs, color: c.text, flex: 1 },
  changeNoticeUndo: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.primary },
  changeOptionRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.sm, padding: spacing.sm, borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.md },
  changeOptionName: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  changeOptionMeta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  changeTypeBadge: { paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: borderRadius.full },
  changeTypeText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium },

  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md, marginBottom: spacing.sm },

  bundleBarContainer: { height: 8, backgroundColor: c.borderLight, borderRadius: 4, marginBottom: spacing.sm, overflow: 'hidden' },
  bundleBarFill: { height: '100%', backgroundColor: c.primary, borderRadius: 4 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
